import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEdit, FaPlus } from 'react-icons/fa';

import {
  addRestaurantService,
  fetchRestaurantServices,
  updateRestaurantService,
} from './restaurantApi';
import PartyHallServiceFields from './PartyHallServiceFields';
import {
  CAPACITY_RESTAURANT_SERVICE_TYPES,
  DINEIN_RESTAURANT_SERVICE_TYPES,
  PARTY_HALL_SERVICE_TYPE,
  RESTAURANT_SERVICE_LABELS,
  RESTAURANT_SERVICE_TYPES,
  buildServicePayload,
  emptyServiceForm,
  extractPartyHallFields,
  formatHallType,
  formatNoOfTables,
  validateServicePayload,
} from './restaurantServiceConstants';
import { canManageRestaurantServices } from '../utils/eatsAppAuth';
import { canEditRestaurantStoreProfile } from './deliveryAgentSession';
import './css/restaurantServices.css';

const formatServiceLabel = (serviceType) =>
  RESTAURANT_SERVICE_LABELS[serviceType] || serviceType || '—';

const RestaurantServicesDisplay = ({ restaurantId, onServicesChange, reloadToken = 0, hideTitle = false }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyServiceForm());
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(emptyServiceForm);
  const [saving, setSaving] = useState(false);
  const canEditProfile = canEditRestaurantStoreProfile();
  const canManageServices = useMemo(
    () => canEditProfile && canManageRestaurantServices(),
    [canEditProfile]
  );

  const loadServices = useCallback(async () => {
    const id = restaurantId != null ? String(restaurantId).trim() : '';
    if (!id || id === 'null' || id === 'undefined') {
      setServices([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const rows = await fetchRestaurantServices(id);
      setServices(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Failed to load restaurant services:', e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load restaurant services.'
      );
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadServices();
  }, [loadServices, reloadToken]);

  const configuredTypes = useMemo(
    () =>
      new Set(
        services.map((row) => String(row.serviceType || '').trim().toUpperCase()).filter(Boolean)
      ),
    [services]
  );

  const availableServiceTypes = useMemo(
    () => RESTAURANT_SERVICE_TYPES.filter((type) => !configuredTypes.has(type)),
    [configuredTypes]
  );

  const resetAddForm = useCallback(() => {
    setAddForm({
      ...emptyServiceForm(),
      serviceType: availableServiceTypes[0] || '',
    });
  }, [availableServiceTypes]);

  useEffect(() => {
    if (!showAddForm) return;
    setAddForm((current) => {
      const nextType =
        current.serviceType && availableServiceTypes.includes(current.serviceType)
          ? current.serviceType
          : availableServiceTypes[0] || '';
      if (nextType === current.serviceType) return current;
      return { ...current, serviceType: nextType };
    });
  }, [availableServiceTypes, showAddForm]);

  const startEdit = (row) => {
    const serviceType = String(row.serviceType || '').toUpperCase();
    const min = row.minGuestCount ?? row.guestCount;
    const hall = extractPartyHallFields(row);
    setShowAddForm(false);
    setEditingId(row.restaurantServiceId);
    setEditForm({
      ...emptyServiceForm(),
      serviceType,
      status: String(row.status || 'ACTIVE').trim().toUpperCase(),
      minGuestCount: min != null ? String(min) : '',
      maxGuestCount: row.maxGuestCount != null ? String(row.maxGuestCount) : '',
      noOfTables: row.noOfTables != null && row.noOfTables !== '' ? String(row.noOfTables) : '',
      ...hall,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyServiceForm());
  };

  const openAddForm = () => {
    cancelEdit();
    resetAddForm();
    setShowAddForm(true);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setAddForm(emptyServiceForm());
  };

  const saveRow = async (row) => {
    const serviceType = String(row.serviceType || '').toUpperCase();
    const isCapacityService = CAPACITY_RESTAURANT_SERVICE_TYPES.has(serviceType);
    const isDineInService = DINEIN_RESTAURANT_SERVICE_TYPES.has(serviceType);
    const payload = {};

    if (canManageServices) {
      payload.status = editForm.status;
    }

    if (isCapacityService) {
      const minGuestCount = Number(editForm.minGuestCount);
      if (!Number.isFinite(minGuestCount) || minGuestCount <= 0) {
        alert('Min guest count must be greater than 0.');
        return;
      }

      payload.minGuestCount = minGuestCount;
      if (String(editForm.maxGuestCount || '').trim() !== '') {
        const maxGuestCount = Number(editForm.maxGuestCount);
        if (!Number.isFinite(maxGuestCount) || maxGuestCount < minGuestCount) {
          alert('Max guest count must be greater than or equal to min guest count.');
          return;
        }
        payload.maxGuestCount = maxGuestCount;
      } else {
        payload.maxGuestCount = null;
      }
    }

    if (isDineInService) {
      const noOfTables = Number(editForm.noOfTables);
      if (!Number.isFinite(noOfTables) || noOfTables <= 0) {
        alert('Number of tables must be greater than 0.');
        return;
      }
      payload.noOfTables = noOfTables;
    }

    setSaving(true);
    try {
      await updateRestaurantService(row.restaurantServiceId, payload);
      cancelEdit();
      await loadServices();
      if (typeof onServicesChange === 'function') {
        onServicesChange();
      }
    } catch (e) {
      console.error('Failed to update service:', e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to update service.'
      );
    } finally {
      setSaving(false);
    }
  };

  const saveNewService = async () => {
    const serviceType = String(addForm.serviceType || '').trim().toUpperCase();
    if (!serviceType) {
      alert('Select a service type.');
      return;
    }

    const validationError = validateServicePayload(serviceType, addForm);
    if (validationError) {
      alert(validationError);
      return;
    }

    const id = restaurantId != null ? String(restaurantId).trim() : '';
    if (!id) return;

    setSaving(true);
    try {
      await addRestaurantService(id, buildServicePayload(serviceType, addForm));
      closeAddForm();
      await loadServices();
      if (typeof onServicesChange === 'function') {
        onServicesChange();
      }
    } catch (e) {
      console.error('Failed to add service:', e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to add service.'
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStatus = (status, isEditing) => {
    if (isEditing && canManageServices) {
      return (
        <select
          className="restaurant-services-status-select"
          value={editForm.status}
          onChange={(e) => setEditForm((current) => ({ ...current, status: e.target.value }))}
          disabled={saving}
          aria-label="Service status"
        >
          <option value="ACTIVE">ACTIVE</option>
          <option value="INACTIVE">INACTIVE</option>
        </select>
      );
    }

    return (
      <span className={`restaurant-detail-badge restaurant-detail-badge-${status.toLowerCase()}`}>
        {status}
      </span>
    );
  };

  const addFormIsCapacity = CAPACITY_RESTAURANT_SERVICE_TYPES.has(
    String(addForm.serviceType || '').toUpperCase()
  );
  const addFormIsDineIn = DINEIN_RESTAURANT_SERVICE_TYPES.has(
    String(addForm.serviceType || '').toUpperCase()
  );
  const addFormIsPartyHall =
    String(addForm.serviceType || '').toUpperCase() === PARTY_HALL_SERVICE_TYPE;

  return (
    <div className="restaurant-detail-card restaurant-detail-services-card">
      <div
        className={`restaurant-detail-card-header restaurant-services-card-header${
          hideTitle ? ' restaurant-detail-card-header--badge-only' : ''
        }`}
      >
        {hideTitle ? null : <h3 className="restaurant-detail-section-title">Services</h3>}
        {canManageServices && availableServiceTypes.length > 0 ? (
          <button
            type="button"
            className="restaurant-detail-btn-save restaurant-services-add-btn"
            onClick={openAddForm}
            disabled={saving || showAddForm}
          >
            <FaPlus aria-hidden="true" />
            <span>Add service</span>
          </button>
        ) : null}
      </div>

      {loading ? <p className="restaurant-detail-message">Loading services…</p> : null}
      {!loading && error ? <p className="restaurant-detail-message error">{error}</p> : null}

      {!loading && !error && showAddForm ? (
        <div className="restaurant-services-form">
          <div className="restaurant-services-form-grid">
            <label className="restaurant-services-picker-field">
              <span className="restaurant-services-picker-field-label">Service</span>
              <select
                value={addForm.serviceType}
                onChange={(e) => setAddForm((current) => ({ ...current, serviceType: e.target.value }))}
                disabled={saving}
              >
                {availableServiceTypes.map((type) => (
                  <option key={type} value={type}>
                    {RESTAURANT_SERVICE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </label>
            <label className="restaurant-services-picker-field">
              <span className="restaurant-services-picker-field-label">Status</span>
              <select
                value={addForm.status}
                onChange={(e) => setAddForm((current) => ({ ...current, status: e.target.value }))}
                disabled={saving}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </label>
            {addFormIsCapacity ? (
              <>
                <label className="restaurant-services-picker-field">
                  <span className="restaurant-services-picker-field-label">Min Guests</span>
                  <input
                    type="number"
                    min="1"
                    value={addForm.minGuestCount}
                    onChange={(e) =>
                      setAddForm((current) => ({ ...current, minGuestCount: e.target.value }))
                    }
                    disabled={saving}
                    placeholder="Minimum guests"
                  />
                </label>
                <label className="restaurant-services-picker-field">
                  <span className="restaurant-services-picker-field-label">Max Guests</span>
                  <input
                    type="number"
                    min="1"
                    value={addForm.maxGuestCount}
                    onChange={(e) =>
                      setAddForm((current) => ({ ...current, maxGuestCount: e.target.value }))
                    }
                    disabled={saving}
                    placeholder="Optional maximum"
                  />
                </label>
              </>
            ) : null}
            {addFormIsDineIn ? (
              <label className="restaurant-services-picker-field">
                <span className="restaurant-services-picker-field-label">No. of Tables</span>
                <input
                  type="number"
                  min="1"
                  value={addForm.noOfTables}
                  onChange={(e) =>
                    setAddForm((current) => ({ ...current, noOfTables: e.target.value }))
                  }
                  disabled={saving}
                  placeholder="Enter number of tables"
                />
              </label>
            ) : null}
            {addFormIsPartyHall ? (
              <PartyHallServiceFields value={addForm} onChange={setAddForm} disabled={saving} />
            ) : null}
          </div>
          <div className="restaurant-services-form-actions">
            <button
              type="button"
              className="restaurant-detail-btn-cancel"
              onClick={closeAddForm}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="restaurant-detail-btn-save"
              onClick={saveNewService}
              disabled={saving || !addForm.serviceType}
            >
              {saving ? 'Saving…' : 'Save Service'}
            </button>
          </div>
        </div>
      ) : null}

      {!loading && !error ? (
        services.length === 0 ? (
          <p className="restaurant-services-display-empty">
            {canManageServices
              ? 'No services configured. Use Add service to configure offerings for this restaurant.'
              : 'No services configured.'}
          </p>
        ) : (
          <div className="restaurant-detail-address-wrap">
            <table className="restaurant-services-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Min Guests</th>
                  <th>Max Guests</th>
                  <th>Tables</th>
                  <th>Hall Type</th>
                  <th>Status</th>
                  {canEditProfile ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {services.map((row) => {
                  const serviceType = String(row.serviceType || '').toUpperCase();
                  const isCapacityService = CAPACITY_RESTAURANT_SERVICE_TYPES.has(serviceType);
                  const isDineInService = DINEIN_RESTAURANT_SERVICE_TYPES.has(serviceType);
                  const isPartyHall = serviceType === PARTY_HALL_SERVICE_TYPE;
                  const status = String(row.status || 'ACTIVE').trim().toUpperCase();
                  const isEditing = editingId === row.restaurantServiceId;
                  const canEditRow =
                    canEditProfile &&
                    (canManageServices || isCapacityService || isDineInService);

                  return (
                    <tr key={row.restaurantServiceId}>
                      <td>{formatServiceLabel(row.serviceType)}</td>
                      <td>
                        {isCapacityService ? (
                          isEditing ? (
                            <input
                              type="number"
                              min="1"
                              className="restaurant-services-guest-input"
                              value={editForm.minGuestCount}
                              onChange={(e) =>
                                setEditForm((current) => ({
                                  ...current,
                                  minGuestCount: e.target.value,
                                }))
                              }
                              disabled={saving}
                              aria-label="Min guest count"
                            />
                          ) : (
                            row.minGuestCount ?? row.guestCount ?? '—'
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {isCapacityService ? (
                          isEditing ? (
                            <input
                              type="number"
                              min="1"
                              className="restaurant-services-guest-input"
                              value={editForm.maxGuestCount}
                              onChange={(e) =>
                                setEditForm((current) => ({
                                  ...current,
                                  maxGuestCount: e.target.value,
                                }))
                              }
                              disabled={saving}
                              aria-label="Max guest count"
                              placeholder="Optional"
                            />
                          ) : (
                            row.maxGuestCount != null ? row.maxGuestCount : '—'
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {isDineInService ? (
                          isEditing ? (
                            <input
                              type="number"
                              min="1"
                              className="restaurant-services-guest-input"
                              value={editForm.noOfTables}
                              onChange={(e) =>
                                setEditForm((current) => ({
                                  ...current,
                                  noOfTables: e.target.value,
                                }))
                              }
                              disabled={saving}
                              aria-label="Number of tables"
                            />
                          ) : (
                            formatNoOfTables(row)
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{isPartyHall ? formatHallType(row) : '—'}</td>
                      <td>{renderStatus(status, isEditing)}</td>
                      {canEditProfile ? (
                        <td>
                          <div className="restaurant-services-row-actions">
                            {canEditRow ? (
                              isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className="restaurant-services-guest-save"
                                    onClick={() => saveRow(row)}
                                    disabled={saving}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="restaurant-services-guest-cancel"
                                    onClick={cancelEdit}
                                    disabled={saving}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="restaurant-services-guest-edit-btn"
                                  onClick={() => startEdit(row)}
                                  disabled={
                                    saving ||
                                    showAddForm ||
                                    (editingId != null && editingId !== row.restaurantServiceId)
                                  }
                                  title="Edit service"
                                  aria-label="Edit service"
                                >
                                  <FaEdit aria-hidden="true" />
                                </button>
                              )
                            ) : (
                              '—'
                            )}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
};

export default RestaurantServicesDisplay;
